import axios from "../axios";

// The AI ingest endpoints on the Java backend (OrganizationQuestionnaireAiIngestController).
//
// Separate from organization-questionnaire.js because the whole payload is built by the data-sorting
// service and posted through here unmodified — see pages/aiDataMapping/api/dataMappingClient.js's
// buildCommitPayload. Keeping it apart makes it obvious that `request` is not something this file shapes.
const api = {
  // The output structure for a target. The agent reads this server-side for mapping; the frontend reads it
  // for display — the AI Mapping task bar shows the destination and the reporting period from it, which is
  // why it is fetched from Java rather than from the data-sorting session (where it only appears once the
  // agent has called get_task_template).
  getTemplate({ questionnaireId, target }, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` },
      params: { target },
    };
    return axios.get(`/organization-questionnaires/${questionnaireId}/ai-ingest/template`, config)
      .then(({ data }) => data);
  },

  // Every AI-assisted ingest into this task, newest first. Summaries only: the server nulls the row-id
  // lists, the step log and the audit trail, all of which grow without bound.
  getRuns({ questionnaireId }, accessToken) {
    const config = { headers: { "Authorization": `Bearer ${accessToken}` } };
    return axios.get(`/organization-questionnaires/${questionnaireId}/ai-ingest/runs`, config)
      .then(({ data }) => data);
  },

  // One run in full — the audit trail and the step log it was rendered from. This is the record
  // the methodology card's "Full trail: ai-ingest run #N" line points at; until it was fetched by
  // something, that line referred to a record the product gave no way to open.
  getRun({ questionnaireId, runId }, accessToken) {
    const config = { headers: { "Authorization": `Bearer ${accessToken}` } };
    return axios.get(`/organization-questionnaires/${questionnaireId}/ai-ingest/runs/${runId}`, config)
      .then(({ data }) => data);
  },

  // One page of the rows already in a target, in the mapped output's shape — descriptor field names as
  // columns, labels not ids — so the commit preview's Existing / After commit views can sit beside Ready to
  // commit and line up column for column. Same envelope as data-sorting's GET /dataframes/{key}
  // ({columns, rows, total_rows, page, page_size, total_pages}); `page` is 1-based.
  getExistingRows({ questionnaireId, target, page = 1, size = 100 }, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` },
      params: { target, page, size },
    };
    return axios.get(`/organization-questionnaires/${questionnaireId}/ai-ingest/existing-rows`, config)
      .then(({ data }) => data);
  },

  // Validate or commit a mapped payload. `dryRun: true` writes nothing and returns the same per-row
  // errors a commit would, which is what lets the user see the outcome before choosing it.
  //
  // `request` is passed straight through: it is AiIngestRequestDto, assembled by the data-sorting
  // service from the frame the user reviewed. Reshaping it here would mean two places to change and a
  // silent drift between them.
  ingest({ questionnaireId, request, dryRun = false }, accessToken) {
    const config = {
      headers: { "Authorization": `Bearer ${accessToken}` },
      params: { dryRun },
    };
    return axios.post(`/organization-questionnaires/${questionnaireId}/ai-ingest`, request, config)
      .then(({ data }) => data);
  },
};

export default api;
